import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getLocalStorage, clearLocalStorages } from '@/utils/storages';
import { SERVICES } from '@/configs';
import { SelectedDevice } from '@/pages/Dashboard/Dashboard';

type UseDeviceParams = {
  ndmain?: string;
  email?: string;
  page?: number;
  size?: number;
  download?: boolean;
  device_uuid?: string;
  sc_number?: string;
};

// Helper function to handle 401 errors
const handle401Error = (error: any) => {
  if (error?.response?.status === 401 || error?.response?.data?.code === 401) {
    clearLocalStorages();
    window.location.href = '/';
    return true;
  }
  return false;
};

async function fetchDevice({ queryKey }: { queryKey: [string, UseDeviceParams] }): Promise<string> {
  const [, params] = queryKey;
  const token = getLocalStorage('access_token');

  const filteredParams = { ...params };
  
  if (filteredParams.ndmain === undefined || filteredParams.ndmain === '') {
    delete filteredParams.ndmain;
  }

  if (filteredParams.device_uuid === undefined || filteredParams.device_uuid === '') {
    delete filteredParams.device_uuid;
  }

  if (filteredParams.email === undefined || filteredParams.email === '') {
    delete filteredParams.email;
  }

  if (filteredParams.sc_number === undefined || filteredParams.sc_number === '') {
    delete filteredParams.sc_number;
  }

  const options = {
    method: 'GET',
    url: SERVICES.LIST_DEVICE,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: filteredParams,
  };

  try {
    const res = await axios(options);

    if (res?.data?.code === 200) {
      return params?.download ? res?.data?.data?.url : res?.data?.data;
    }
    
    if (res?.data?.code === 401) {
      clearLocalStorages();
      window.location.href = '/';
      throw new Error('Unauthorized');
    }
    
    throw new Error('Failed to fetch device data');
  } catch (error: any) {
    if (handle401Error(error)) {
      throw new Error('Unauthorized');
    }
    throw error;
  }
}

export function useDevice(params: UseDeviceParams) {
  const token = getLocalStorage('access_token');
  const enabled =
    !!token &&
    (!!params.email || !!params.device_uuid || !!params.sc_number);

  return useQuery({
    queryKey: ['devices', params],
    queryFn: fetchDevice,
    enabled,
  });
};

export function useUnpairDevice(devices: SelectedDevice[]) {
  const accessToken = getLocalStorage('access_token');

  return useMutation({
    mutationFn: async () => {
      try {
        const res = await axios.delete(SERVICES.DEVICE, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          data: {
            devices
          }
        });
        
        if (res?.data?.code === 401) {
          clearLocalStorages();
          window.location.href = '/';
          throw new Error('Unauthorized');
        }
        
        return res?.data;
      } catch (error: any) {
        if (handle401Error(error)) {
          throw new Error('Unauthorized');
        }
        throw error;
      }
    },
  });
};

export function useIsolirDevice(devices: SelectedDevice[], evidence?: string) {
  const accessToken = getLocalStorage('access_token');

  return useMutation({
    mutationFn: async () => {
      try {
        const res = await axios.put(
          SERVICES.ISOLIR,
          { devices, evidence: evidence?.split(',')[1] },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        
        if (res?.data?.code === 401) {
          clearLocalStorages();
          window.location.href = '/';
          throw new Error('Unauthorized');
        }
        
        return res?.data;
      } catch (error: any) {
        if (handle401Error(error)) {
          throw new Error('Unauthorized');
        }
        throw error;
      }
    },
  });
};

export function useDownloadDevice(params: UseDeviceParams) {
  return useMutation({
    mutationFn: async () => {
      try {
        const res = await fetchDevice({
          queryKey: ['', params]
        });
        
        return res;
      } catch (error: any) {
        if (handle401Error(error)) {
          throw new Error('Unauthorized');
        }
        throw error;
      }
    },
  });
};
